<script setup lang="ts">
const props = defineProps<{
  unit: number
  price: string
  priceWithDiscount?: string
  canAdd: boolean
  editing: boolean
  disableAdd?: boolean
  disableSubstract?: boolean
  isUnavailable?: boolean
}>()

defineEmits(['update:unit', 'add', 'continue', 'save'])
const isMobile = inject<boolean>('isMobile')
const restaurantStore = useRestaurantStore()
const { t } = useLocale()

const showOriginalPriceStriked = computed(() => {
  return props.priceWithDiscount && props.price?.trim() !== props.priceWithDiscount.trim()
})

const unitState = useVModel(props, 'unit')
const limitProductOrder = restaurantStore.restaurant?.limits.limitProductOrder || 10
</script>

<template>
  <section
    class="fixed bottom-0 left-0 z-[9] w-full md:relative md:rounded-none md:shadow-none"
    :class="[
      isUnavailable && isMobile ? 'rounded-none' : 'rounded-t-3xl bg-white shadow-elevation-up',
    ]"
  >
    <div
      v-if="!isUnavailable || !isMobile"
      class="my-4 flex flex-row items-center justify-between gap-2 px-4 md:p-0"
    >
      <QuantitySelector
        v-model:count="unitState"
        :min="1"
        :max="limitProductOrder"
        :show-delete="false"
        :disable-add="!!disableAdd"
        :disable-substract="!!disableSubstract"
      />
      <div class="flex flex-row flex-nowrap gap-2 text-2xl md:flex-wrap md:text-3xl">
        <p
          v-if="showOriginalPriceStriked"
          data-test="product-m__price--promoted"
          class="line-through md:ml-auto"
        >
          {{ price }}
        </p>
        <p class="font-bold md:ml-auto" data-test="product-m__price">
          {{ priceWithDiscount ? priceWithDiscount : price }}
        </p>
      </div>
    </div>
    <div class="flex h-[64px] w-full flex-row items-center md:mx-auto md:h-fit md:justify-between">
      <template v-if="editing">
        <!-- ProductEdition: 1 button (save) -->
        <button
          class="btn-primary h-full w-full md:h-fit"
          :disabled="!canAdd"
          data-test="btn-product-detail-save"
          :class="{ 'rounded-none': isMobile }"
          @click="$emit('save')"
        >
          {{ t('productDetail.actions.save') }}
        </button>
      </template>
      <!-- Unavailable -->
      <template v-else-if="isUnavailable">
        <!-- ProductDetail: producto no disponible -->
        <button
          class="btn-unavailable h-full md:h-fit md:w-full"
          data-test="btn-product-detail-unavailable"
          :class="{ 'mx-auto mb-8 w-[90%]': isMobile }"
        >
          {{ t('productDetail.actions.unavailable') }}
        </button>
      </template>
      <template v-else>
        <!-- ProductDetail: Mobile 2 buttons, Desktop one button (goToCart, addToCart) -->
        <button
          v-if="isMobile"
          class="btn-secondary h-full w-1/2 md:h-fit md:w-full"
          :disabled="!canAdd"
          data-test="btn-product-detail-pay"
          :class="{ 'rounded-none': isMobile }"
          @click="$emit('continue')"
        >
          {{ t('productDetail.actions.payNow') }}
        </button>
        <button
          class="btn-primary h-full w-1/2 md:h-fit md:w-full"
          :disabled="!canAdd"
          data-test="btn-product-detail-add-to-cart"
          :class="{ 'rounded-none': isMobile }"
          @click="$emit('add')"
        >
          {{ t('productDetail.actions.addToCart') }}
        </button>
      </template>
    </div>
  </section>
</template>
